import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import {
  FeatherAlertCircle,
  FeatherClipboardList,
  FeatherClock,
  FeatherLoader,
} from "@subframe/core";
import { Link } from "@tanstack/react-router";

import { WorkspaceStatusBadge } from "@/components/checklists/WorkspaceStatusBadge";
import { TintedEntityIcon } from "@/components/ui/entity-icon";
import { FollowUpActionResponseDtoStatus } from "@/generated/model";
import type { FollowUpActionWithSync } from "@/hooks/use-offline-follow-up-actions";
import {
  formatActionDueDate,
  isActionOverdue,
} from "@/lib/follow-up-action-utils";
import { followUpActionPill } from "@/lib/workspace/status-pill";

interface FollowUpActionRowProps {
  action: FollowUpActionWithSync;
  isOffline?: boolean;
}

export function FollowUpActionRow({
  action,
  isOffline,
}: FollowUpActionRowProps) {
  // Waiting approval: the work is done, so it isn't late.
  const overdue =
    action.status !== FollowUpActionResponseDtoStatus.WAITING_APPROVAL &&
    isActionOverdue(action);
  const dueDateText = formatActionDueDate(action);

  // Sync badge - show pending or failed sync status
  let syncBadge: React.ReactNode | null = null;
  if (action.localSyncStatus) {
    const { pendingCount, failedCount } = action.localSyncStatus;

    if (failedCount > 0) {
      syncBadge = (
        <Badge variant="error" icon={<FeatherAlertCircle />}>
          Sync failed
        </Badge>
      );
    } else if (pendingCount > 0) {
      syncBadge = !isOffline ? (
        <Badge
          variant="neutral"
          icon={<FeatherLoader className="animate-spin" />}
        >
          Syncing
        </Badge>
      ) : (
        <Badge variant="warning">Pending sync</Badge>
      );
    }
  }

  return (
    <Link
      to="/follow-up-actions/$actionId"
      params={{ actionId: action.id }}
      className="block hover:bg-neutral-50 transition-colors"
    >
      <div className="flex w-full items-center justify-between gap-4 rounded-sm px-4 py-3 mobile:items-start mobile:gap-3 mobile:rounded-none mobile:px-4 mobile:py-4">
        <div className="flex items-center gap-3 grow shrink-0 basis-0 max-w-[50%]">
          <TintedEntityIcon
            entity="follow-up-action"
            size={40}
            fallbackIcon={<FeatherClipboardList />}
          />
          <div className="flex min-w-0 flex-col items-start gap-1">
            <span className="text-body-bold font-body-bold text-neutral-900 mobile:text-large-button mobile:font-large-button truncate w-full">
              {action.description}
            </span>
            <div className="flex items-center gap-1 mobile:gap-1.5">
              {dueDateText ? (
                <>
                  <FeatherClock
                    className={`text-caption font-caption mobile:text-large-button mobile:font-large-button ${overdue ? "text-error-700" : "text-subtext-color"}`}
                  />
                  <span
                    className={`text-caption font-caption mobile:text-body mobile:font-body ${overdue ? "text-error-700" : "text-subtext-color"}`}
                  >
                    {dueDateText}
                  </span>
                </>
              ) : (
                <span className="text-caption font-caption mobile:text-body mobile:font-body text-subtext-color">
                  No due date
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 flex-shrink-0 mobile:flex-col mobile:items-end mobile:gap-2">
          {syncBadge || (
            <WorkspaceStatusBadge pill={followUpActionPill(action)} />
          )}
          <Button className="mobile:hidden" variant="neutral-primary">
            View
          </Button>
        </div>
      </div>
    </Link>
  );
}
